import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { DEMO_BANK_ACCOUNTS } from '@/data/demo-data';
import { usePermissions } from '@/hooks/usePermissions';
import { useApp } from '@/contexts/AppContext';
import { useFormatting } from '@/hooks/useFormatting';
import { getAllBankAccounts } from '@/api/services/bankAccountService';
import type { BankAccount } from '@/types';
import { Landmark, ChevronRight } from 'lucide-react';
import { formatDate } from '@/lib/utils';

interface BankAccountsCardProps {
  onNavigate?: (page: string, data?: { accountFilter?: string }) => void;
}

export default function BankAccountsCard({ onNavigate }: BankAccountsCardProps) {
  const { hasPermission } = usePermissions();
  const { mode } = useApp();
  const { formatCurrency } = useFormatting();
  const asAtDate = formatDate(new Date());
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>(DEMO_BANK_ACCOUNTS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'live') {
      setIsLoading(true);
      setError(null);
      getAllBankAccounts()
        .then((response) => {
          const accounts: BankAccount[] = response.bankAccounts.map((account) => ({
            id: String(account.id),
            name: account.name,
            bank: account.bank,
            balance: account.balance,
            currency: account.currencyCode,
            type: account.accountType ?? undefined,
            dataSourceName: account.dataSourceName ?? undefined,
          }));
          setBankAccounts(accounts);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to fetch bank accounts');
          setBankAccounts([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setBankAccounts(DEMO_BANK_ACCOUNTS);
      setError(null);
    }
  }, [mode]);
  
  if (!hasPermission('view_bank_accounts_card')) {
    return null;
  }
  
  const totalCash = bankAccounts.reduce((sum, acc) => sum + acc.balance, 0);

  return (
    <Card className="h-full flex flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-1">
          <span className="text-lg font-semibold text-neutral-900">Bank Accounts</span>
          <InfoTooltip
            title="Bank Accounts"
            content="Live balances across all connected bank accounts. The total cash position reflects the sum of all accounts. Click any account to view its transactions."
          />
        </div>
        <span className="text-xs text-neutral-500">{asAtDate}</span>
      </div>
      <CardContent className="flex-1 pt-4">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading bank accounts...</div>
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center py-8">
            <div className="text-destructive">{error}</div>
          </div>
        )}
        {!isLoading && !error && bankAccounts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
              style={{ backgroundColor: 'var(--mc-neutral-bg)' }}
            >
              <Landmark className="h-5 w-5" style={{ color: 'var(--mc-neutral-icon)' }} />
            </div>
            <p className="text-sm font-medium text-neutral-900">No bank accounts connected</p>
            <p className="text-xs text-neutral-500 mt-1 max-w-xs">
              Bank accounts are connected via your data sources. Connect one in System Settings → Data Source Config.
            </p>
          </div>
        )}
        {!isLoading && !error && bankAccounts.length > 0 && (
          <div className="space-y-3">
            {bankAccounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center justify-between py-3 border-b border-gray-100 cursor-pointer group last:border-0"
                onClick={() => onNavigate?.('transactions', { accountFilter: account.name })}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--mc-neutral-bg)' }}>
                    <Landmark className="h-4 w-4" style={{ color: 'var(--mc-neutral-icon)' }} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-neutral-900 hover:underline hover:underline-offset-2">{account.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-neutral-500">{account.bank}</span>
                      {account.dataSourceName && (
                        <Badge variant="outline" className="text-xs py-0">
                          {account.dataSourceName}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-neutral-900">{formatCurrency(account.balance)}</div>
                    <div className="text-xs text-neutral-500">{account.currency}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between pt-3 mt-1 border-t-2 border-neutral-200">
              <span className="text-sm font-semibold text-neutral-900">Total Cash Position</span>
              <span className="text-sm font-bold text-neutral-900">{formatCurrency(totalCash)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
